/* eslint-disable @typescript-eslint/no-explicit-any */
// @ts-nocheck
import { useCallback, useEffect, useRef, useState } from 'react';
import beautifyUrl from 'utils/beautifyUrl';
import globalHeaders from 'utils/globalHeaders';
import axios from 'config/api';
import { TokenNotFoundException } from 'utils/exceptions';
import { AxiosError, AxiosResponse } from 'axios';

const configDefaults = {
  headers: {},
  params: {},
  query: {},
  skip: false,
  publicUrl: false,
  blobType: 'application/pdf',
};

interface UseGetConfig<T = any> {
  headers?: Record<string, string>;
  transformSuccess?: (response: any) => T;
  transformFail?: (error: AxiosResponse<unknown>) => unknown;
  params?: Record<string, unknown>;
  query?: Record<string, unknown>;
  onSuccess?: (response: any) => void | Promise<void>;
  onFailure?: (error: any, status?: number) => void;
  skip?: boolean;
  publicUrl?: boolean;
  blobType?: string;
  responseType?: XMLHttpRequestResponseType;
  downloadBlob?: boolean;
  validateStatus?: (status: number) => boolean;
  skipToken?: boolean;
  customSkipFn?: (config: any) => boolean;
  skipLoading?: boolean;
  signal?: AbortSignal;
  path?: string;
  onError?: (error: any) => void;
}
interface UseGetReturn<T> {
  loading: boolean;
  error: unknown;
  data: any;
  fetch: (newConfig?: any) => Promise<void>;
  setData: React.Dispatch<React.SetStateAction<T | undefined>>;
}

function useGet<T = unknown>(
  path: string,
  config: UseGetConfig<T> = {},
): UseGetReturn<T> {
  const {
    // headers = {},
    // transformSuccess,
    transformFail,
    params = {},
    query = {},
    onSuccess,
    onFailure,
    skip = false,
    publicUrl = false,
    // customSkipFn,
    responseType,
    downloadBlob,
    validateStatus,
    blobType = 'application/pdf',
  } = config;

  const [loading, setLoading] = useState(!skip);
  const [data, setData] = useState(undefined);
  const [error, setError] = useState(null);
  const unMounted = useRef(false);

  const fetch = useCallback(
    async (newConfig: Partial<UseGetConfig<T>> = {}) => {
      const currConfig: UseGetConfig<T> = {
        ...configDefaults,
        ...config,
        ...newConfig,
        query: {
          ...(config?.query && config.query),
          ...(newConfig?.query && newConfig.query),
        },
      };

      if (currConfig.customSkipFn) {
        const skip = currConfig.customSkipFn({ ...currConfig });
        if (skip) {
          setLoading(false);
          return;
        }
      }
      if (!newConfig?.skipLoading) setLoading(true);

      try {
        const token = localStorage.getItem('token');
        const tenant = localStorage.getItem('tenant');

        if (!currConfig.skipToken)
          if (!publicUrl && !token) {
            throw new TokenNotFoundException('Token not found');
          }
        const excludeTenantPaths = ['v1.1/tenant/'];
        const headers = {
          ...globalHeaders(!excludeTenantPaths.includes(path)),
        };

        if (path === 'v1.1/tenant/' && currConfig.query?.q && tenant) {
          headers['tenant-id'] = tenant;
        }

        let response = await axios.get(
          beautifyUrl(currConfig?.path || path, currConfig),
          {
            signal: currConfig?.signal,
            headers,
            responseType:
              (responseType as ResponseType) ||
              (downloadBlob ? 'blob' : undefined),
            ...(typeof validateStatus === 'function' && { validateStatus }),
          },
        );
        if (downloadBlob) {
          // create a download anchor tag
          const downloadLink = document.createElement('a');
          downloadLink.target = '_blank';
          downloadLink.download = response.headers['content-disposition'];
          // convert downloaded data to a Blob
          const blob = new Blob([response.data], { type: blobType });

          // create an object URL from the Blob
          const URL = window.URL || window.webkitURL;
          const downloadUrl = URL.createObjectURL(blob);

          // set object URL as the anchor's href
          downloadLink.href = downloadUrl;

          // append the anchor to document body
          document.body.append(downloadLink);

          // fire a click event on the anchor
          downloadLink.click();

          // cleanup: remove element and revoke object URL
          document.body.removeChild(downloadLink);
          setTimeout(() => URL.revokeObjectURL(downloadUrl), 100);

          if (!unMounted.current) {
            setLoading(false);

            if (onSuccess) {
              await Promise.resolve(onSuccess(undefined as T));
            }
            // if (typeof currConfig?.onSuccess === 'function') {
            //   currConfig?.onSuccess();
            // }
          }
        } else {
          if (currConfig?.transformSuccess) {
            response = currConfig?.transformSuccess(response);
          } else {
            response = response.data;
          }

          if (!unMounted.current) {
            setData(response);
            setLoading(false);

            const successCb =
              typeof currConfig?.onSuccess === 'function'
                ? currConfig.onSuccess
                : onSuccess;
            if (typeof successCb === 'function') {
              await Promise.resolve(successCb(response));
            }
          }
        }
      } catch (err) {
        let e = (err as AxiosError)?.response;
        let status = 500;
        if (e) {
          status = e.status;
          if (transformFail) {
            e = transformFail(e);
          } else if (e?.data instanceof Blob) {
            e = e?.data;
          } else {
            e = e?.data?.error || e?.data?.message || 'Server Error';
          }
        } else {
          e = 'Server Error';
        }

        if (!unMounted.current) {
          setData(undefined);
          setError(e);
          if (err?.message !== 'canceled') setLoading(false);

          if (onFailure) {
            onFailure(e, status);
          }
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(params), JSON.stringify(query)],
  );

  useEffect(() => {
    if (!skip) fetch();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetch]);

  useEffect(
    () => () => {
      unMounted.current = true;
    },
    [],
  );

  return {
    loading,
    error,
    data,
    fetch,
    setData,
  };
}

export default useGet;
